/**
 * QOVO — Custom Cookie Consent Engine
 * UK GDPR (DPA 2018) + PECR compliant. Replaces CookieYes.
 *
 * Same engine as Keplin (shared keplin-cookie.js). Qovo differs only in
 * FUNCTIONAL_KEYS, which names this theme's recently-viewed storage key.
 * Two competing consent-default blocks is how consent state ends up
 * ambiguous: keep this the only one, and no Consent Mode Default tag in GTM.
 *
 * Four categories: necessary, functional, analytics, advertisement.
 * CONSENT_VERSION 2 on Qovo re-asks everyone who answered the old six-category
 * banner (version 1).
 * Performance and Uncategorised were removed deliberately. Performance mapped
 * to the same Google signal as Analytics, so two toggles controlled one thing,
 * and Uncategorised advertised that we did not know what we were running.
 *
 * Responsibilities:
 * 1. Set Google Consent Mode v2 DEFAULTS to "denied" before any tag can fire.
 * 2. Explicitly LOAD Shopify's Customer Privacy API via Shopify.loadFeatures().
 *    window.Shopify.customerPrivacy does NOT exist until that call resolves.
 *    This is what gates Shopify app pixels.
 * 3. Persist the choice and push it to both Google and Shopify.
 * 4. Drive the banner and modal, plus a floating reopen icon. The banner
 *    shows to every visitor without a current decision, in every region.
 * 5. Gate the theme's own Functional storage (recently viewed products),
 *    which the theme would otherwise write regardless of consent.
 *
 * A decision is honoured for 12 months, then the visitor is asked again.
 * The Cookie Policy page promises this, so keep CONSENT_MAX_AGE_MS in step.
 *
 * IMPORTANT: the <script> tag for this file must NOT carry async or defer.
 * It has to run to completion before the GTM snippet below it.
 */

(function () {
  "use strict";

  var STORAGE_KEY = "keplin_consent_v1";
  // Version 2: the category model changed from six to four, so anyone holding
  // a decision made against the old model is asked again.
  var CONSENT_VERSION = 2;
  var CONSENT_MAX_AGE_MS = 365 * 24 * 60 * 60 * 1000; // 12 months

  // Theme localStorage keys that belong to the Functional category. Blocked
  // from being written, and cleared, while Functional is not granted.
  var FUNCTIONAL_KEYS = ["__sf_theme_recently"];
  var functionalAllowed = false;

  // A stored decision counts only if it matches the current category model
  // and is less than 12 months old.
  function isCurrentDecision(parsed) {
    if (!parsed || parsed.version !== CONSENT_VERSION) return false;
    var at = Date.parse(parsed.consentedAt);
    return !isNaN(at) && Date.now() - at < CONSENT_MAX_AGE_MS;
  }

  // ---- 0. Google Consent Mode v2: set DEFAULTS immediately ----------------
  window.dataLayer = window.dataLayer || [];
  function gtag() { window.dataLayer.push(arguments); }
  window.gtag = window.gtag || gtag;

  gtag("consent", "default", {
    ad_storage: "denied",
    ad_user_data: "denied",
    ad_personalization: "denied",
    analytics_storage: "denied",
    functionality_storage: "denied",
    personalization_storage: "denied",
    security_storage: "granted",
    wait_for_update: 2000
  });
  gtag("set", "ads_data_redaction", true);

  // url_passthrough MUST be true. Without it a visitor who clicks an ad and
  // lands on a collection page loses their gclid the moment they click through
  // to a product, because consent is denied so no cookie can hold it.
  gtag("set", "url_passthrough", true);

  // ---- 0b. Replay a stored decision synchronously -------------------------
  // Must run before anything can fire. Do NOT wait on DOM or Shopify here:
  // initUI() waits for both DOMContentLoaded and the Shopify.loadFeatures
  // callback, and if that lands past wait_for_update the tag has already sent
  // a redacted ping and the grant arrives too late to matter.
  (function replayStoredConsentEarly() {
    try {
      var parsed = JSON.parse(localStorage.getItem(STORAGE_KEY) || "null");
      if (!isCurrentDecision(parsed)) return;
      var s = parsed.categories || {};
      functionalAllowed = !!s.functional;
      gtag("consent", "update", {
        ad_storage: s.advertisement ? "granted" : "denied",
        ad_user_data: s.advertisement ? "granted" : "denied",
        ad_personalization: s.advertisement ? "granted" : "denied",
        analytics_storage: s.analytics ? "granted" : "denied",
        functionality_storage: s.functional ? "granted" : "denied",
        personalization_storage: s.functional ? "granted" : "denied",
        security_storage: "granted"
      });
    } catch (e) {
      /* no stored decision, or localStorage blocked — defaults stand */
    }
  })();

  // ---- 0c. Gate the theme's Functional storage ----------------------------
  // Runs before the theme's scripts, so wrapping setItem here catches their
  // writes. Every other key passes straight through.
  function clearFunctionalStorage() {
    FUNCTIONAL_KEYS.forEach(function (k) {
      try { localStorage.removeItem(k); } catch (e) {}
    });
  }

  try {
    var nativeSetItem = Storage.prototype.setItem;
    Storage.prototype.setItem = function (key) {
      if (!functionalAllowed && FUNCTIONAL_KEYS.indexOf(String(key)) !== -1 && this === window.localStorage) {
        return;
      }
      return nativeSetItem.apply(this, arguments);
    };
  } catch (e) {
    /* Storage unavailable — nothing to gate */
  }
  if (!functionalAllowed) clearFunctionalStorage();

  // ---- 1. Category model ----
  var DEFAULT_STATE = {
    necessary: true,       // always on, never asked
    functional: false,
    analytics: false,
    advertisement: false
  };

  var ALL_ON = { necessary: true, functional: true, analytics: true, advertisement: true };

  function readStoredConsent() {
    try {
      var raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return null;
      var parsed = JSON.parse(raw);
      if (!isCurrentDecision(parsed)) return null; // model changed or expired, re-ask
      return parsed;
    } catch (e) {
      return null;
    }
  }

  function writeStoredConsent(state) {
    var payload = {
      version: CONSENT_VERSION,
      consentedAt: new Date().toISOString(),
      categories: state
    };
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
    } catch (e) {
      /* localStorage unavailable — degrade silently, banner will re-show */
    }
    var maxAge = 60 * 60 * 24 * 365; // 1 year
    document.cookie =
      "keplin_consent=" +
      encodeURIComponent(JSON.stringify(state)) +
      "; path=/; max-age=" + maxAge + "; SameSite=Lax";
  }

  // ---- 2. Push consent to Google Consent Mode ----
  function pushGoogleConsent(state) {
    gtag("consent", "update", {
      ad_storage: state.advertisement ? "granted" : "denied",
      ad_user_data: state.advertisement ? "granted" : "denied",
      ad_personalization: state.advertisement ? "granted" : "denied",
      analytics_storage: state.analytics ? "granted" : "denied",
      functionality_storage: state.functional ? "granted" : "denied",
      personalization_storage: state.functional ? "granted" : "denied",
      security_storage: "granted"
    });
  }

  // ---- 3. Shopify Customer Privacy API — explicit load required ----
  var shopifyPrivacyReady = false;

  function loadShopifyPrivacy(onDone) {
    if (window.Shopify && typeof window.Shopify.loadFeatures === "function") {
      window.Shopify.loadFeatures(
        [{ name: "consent-tracking-api", version: "0.1" }],
        function (error) {
          if (!error) shopifyPrivacyReady = true;
          onDone();
        }
      );
    } else {
      // window.Shopify not injected yet (we run before it) — retry briefly.
      setTimeout(function () { loadShopifyPrivacy(onDone); }, 150);
    }
  }

  function pushShopifyConsent(state) {
    if (!(shopifyPrivacyReady && window.Shopify && window.Shopify.customerPrivacy)) return;
    window.Shopify.customerPrivacy.setTrackingConsent(
      {
        analytics: state.analytics,
        marketing: state.advertisement,
        preferences: state.functional
      },
      function () {
        /* Shopify persisted its own consent record */
      }
    );
  }

  function applyConsent(state) {
    functionalAllowed = !!state.functional;
    if (!functionalAllowed) clearFunctionalStorage();
    writeStoredConsent(state);
    pushGoogleConsent(state);
    pushShopifyConsent(state);
  }

  // ---- 4. Coordinate DOM-ready + Shopify-privacy-ready, then init UI once --
  var domReady = false;
  var uiInited = false;

  function ready(fn) {
    if (document.readyState !== "loading") fn();
    else document.addEventListener("DOMContentLoaded", fn);
  }

  function tryFinalize() {
    if (!domReady || uiInited) return;
    uiInited = true;
    initUI();
  }

  function initUI() {
    var banner = document.getElementById("kc-banner");
    var modal = document.getElementById("kc-modal");
    var floatBtn = document.getElementById("kc-float-btn");
    if (!banner || !modal) return;

    var toggles = modal.querySelectorAll(".kc-toggle__input");

    function setTogglesFromState(state) {
      toggles.forEach(function (t) {
        var cat = t.getAttribute("data-category");
        t.checked = !!state[cat];
      });
    }

    function stateFromToggles() {
      var state = Object.assign({}, DEFAULT_STATE);
      toggles.forEach(function (t) {
        var cat = t.getAttribute("data-category");
        state[cat] = t.checked;
      });
      return state;
    }

    function showFloatBtn() { if (floatBtn) floatBtn.classList.add("kc-float-visible"); }
    function hideFloatBtn() { if (floatBtn) floatBtn.classList.remove("kc-float-visible"); }

    function showBanner() {
      hideFloatBtn(); // never overlap the banner with the reopen icon
      banner.style.display = "block";
      banner.setAttribute("aria-hidden", "false");
      requestAnimationFrame(function () { banner.classList.add("kc-visible"); });
    }

    function hideBanner() {
      banner.classList.remove("kc-visible");
      banner.setAttribute("aria-hidden", "true");
      setTimeout(function () { banner.style.display = "none"; }, 350);
      showFloatBtn(); // consent decided — surface the reopen icon
    }

    function openModal() {
      var stored = readStoredConsent();
      setTogglesFromState(stored ? stored.categories : DEFAULT_STATE);
      modal.style.display = "flex";
      modal.setAttribute("aria-hidden", "false");
      requestAnimationFrame(function () { modal.classList.add("kc-visible"); });
      document.body.style.overflow = "hidden";
    }

    function closeModal() {
      modal.classList.remove("kc-visible");
      modal.setAttribute("aria-hidden", "true");
      document.body.style.overflow = "";
      setTimeout(function () { modal.style.display = "none"; }, 220);
    }

    // Banner buttons
    var acceptAllBtn = document.getElementById("kc-accept-all-btn");
    var rejectAllBtn = document.getElementById("kc-reject-all-btn");
    var manageBtn = document.getElementById("kc-manage-btn");

    if (acceptAllBtn) acceptAllBtn.addEventListener("click", function () {
      applyConsent(Object.assign({}, ALL_ON));
      hideBanner();
    });

    if (rejectAllBtn) rejectAllBtn.addEventListener("click", function () {
      applyConsent(Object.assign({}, DEFAULT_STATE));
      hideBanner();
    });

    if (manageBtn) manageBtn.addEventListener("click", openModal);

    // Floating reopen icon reopens the small banner, not the modal. If the
    // visitor wants to change categories, the banner's Customise button does it.
    if (floatBtn) floatBtn.addEventListener("click", showBanner);

    // Modal buttons
    var modalAccept = document.getElementById("kc-modal-accept-btn");
    var modalReject = document.getElementById("kc-reject-btn");
    var modalSave = document.getElementById("kc-save-btn");
    var modalClose = document.getElementById("kc-modal-close-btn");
    var backdrop = document.getElementById("kc-backdrop");
    var showMoreBtn = document.getElementById("kc-show-more-btn");
    var descMore = document.getElementById("kc-desc-more");

    if (modalAccept) modalAccept.addEventListener("click", function () {
      applyConsent(Object.assign({}, ALL_ON));
      closeModal();
      hideBanner();
    });

    if (modalReject) modalReject.addEventListener("click", function () {
      applyConsent(Object.assign({}, DEFAULT_STATE));
      closeModal();
      hideBanner();
    });

    if (modalSave) modalSave.addEventListener("click", function () {
      applyConsent(stateFromToggles());
      closeModal();
      hideBanner();
    });

    if (modalClose) modalClose.addEventListener("click", closeModal);
    if (backdrop) backdrop.addEventListener("click", closeModal);

    if (showMoreBtn && descMore) showMoreBtn.addEventListener("click", function () {
      descMore.classList.toggle("kc-expanded");
      showMoreBtn.textContent = descMore.classList.contains("kc-expanded") ? "Show less" : "Show more";
    });

    document.addEventListener("keydown", function (e) {
      if (e.key === "Escape" && modal.classList.contains("kc-visible")) closeModal();
    });

    // Public API, for a footer "Manage Cookies" link:
    // <a href="#" onclick="window.KeplinConsent.open(); return false;">Manage Cookies</a>
    window.KeplinConsent = {
      open: openModal,
      getState: function () {
        var stored = readStoredConsent();
        return stored ? stored.categories : null;
      },
      reset: function () {
        try { localStorage.removeItem(STORAGE_KEY); } catch (e) {}
        document.cookie = "keplin_consent=; path=/; max-age=0";
        hideFloatBtn();
      }
    };

    // On load: replay a stored decision, or show the banner.
    // For Google this duplicates section 0b, which already fired synchronously.
    // Kept because it also pushes to Shopify's API, which cannot run that early.
    var stored = readStoredConsent();
    if (stored) {
      pushGoogleConsent(stored.categories);
      pushShopifyConsent(stored.categories);
      showFloatBtn();
    } else {
      // No current decision: always ask, in every region. Shopify's own
      // shouldShowBanner() is region-based and loads asynchronously, so using
      // it made the banner appear or not depending on load timing, and never
      // outside the regions ticked in Settings > Customer privacy.
      showBanner();
    }
  }

  ready(function () { domReady = true; tryFinalize(); });
  // When Shopify's API becomes ready, hand it any decision already made. The
  // visitor may have clicked before the API loaded, and initUI's replay may
  // have run before it too; either way Shopify would otherwise never hear it.
  loadShopifyPrivacy(function () {
    var stored = readStoredConsent();
    if (stored) pushShopifyConsent(stored.categories);
    tryFinalize();
  });
})();
